import { DataLayerAnalytics } from '../DataLayerAnalytics';
import { DataLayerEvent, DataLayerItemsListName, OrderStep, Recommendation, ViewListItem } from '../types';
import { DataLayerService, OrderType } from './types';
import { FlightSegment } from '@websky/graphql';

abstract class OrderDataLayer<T = {}> {
	protected readonly dataLayer: DataLayerAnalytics;
	protected order: T;
	protected initialized: boolean;

	protected constructor(instance: DataLayerAnalytics) {
		this.dataLayer = instance;
	}

	public abstract getFlightSegments(): FlightSegment[];

	public abstract getOrderStatus(): OrderStep;

	public abstract getOrderType(): OrderType;

	public pushToDataLayer(
		eventType: DataLayerEvent,
		services: DataLayerService[],
		itemListName: DataLayerItemsListName = null,
		params: object = {}
	) {
		if (this.isInitialized() && this.dataLayer) {
			try {
				this.dataLayer.pushToDataLayer(
					eventType,
					services
						.reduce<DataLayerService[]>((newServices, service) => {
							if (service.forAllSegments) {
								return [
									...newServices,
									...this.getFlightSegments().map((segment, key) => ({
										...service,
										price: key === 0 ? service.price : 0,
										segmentId: segment.id
									}))
								];
							}

							return [...newServices, service];
						}, [])
						.map(service => this.getEcommerceItem(service)),
					params,
					itemListName
				);
			} catch (e) {
				console.error(e);
			}
		}
	}

	public isInitialized() {
		return this.initialized;
	}

	public setOrderStatus() {
		this.dataLayer.setCommonParams({
			item_category2: this.getOrderStatus()
		});
	}

	public setOrder(order: T) {
		this.order = order;

		this.setOrderStatus();

		this.initialized = true;
	}

	public getOrder() {
		return this.order;
	}

	public getEcommerceItem(service: DataLayerService): ViewListItem {
		const segmentIds = service.segmentIds ? service.segmentIds : [service.segmentId ?? '0'],
			segments = this.getFlightSegments().filter(segment => segmentIds.includes(segment.id)),
			firstSegment = segments[0];

		const flightDestinations = segments.map(segment => segment.departure.airport.iata);

		flightDestinations.push(segments[segments.length - 1].arrival.airport.iata);

		return {
			item_brand: flightDestinations.join('-'),
			item_category: service.category,
			item_id: `${firstSegment.marketingAirline.iata}-${firstSegment.flightNumber}`,
			item_name: service.name,
			item_category3: service.isRecommend ? Recommendation.Recommended : Recommendation.NoRecommended,
			price: service.price,
			item_category4: firstSegment.departure.date,
			item_category5: service.travellersTypes || [],
			quantity: service.count ?? null
		};
	}

	public purchaseOrder(transactionId: string, services: DataLayerService[]) {
		this.pushToDataLayer(DataLayerEvent.Purchase, services, null, {
			transaction_id: transactionId
		});
	}

	public setViewItem(service: DataLayerService) {
		this.pushToDataLayer(DataLayerEvent.ViewItem, [service]);
	}

	public beginCheckout(services: DataLayerService[]) {
		this.pushToDataLayer(DataLayerEvent.Checkout, services);
	}

	public setServicesViewItemList(itemListName: DataLayerItemsListName, services: DataLayerService[]) {
		if (!!services.length) {
			this.pushToDataLayer(DataLayerEvent.ViewItemList, services, itemListName);
		}
	}

	public addServicesToCart(services: DataLayerService[]) {
		if (services.length) {
			this.pushToDataLayer(DataLayerEvent.AddToCart, services);
		}
	}

	public removeServicesFromCart(services: DataLayerService[]) {
		if (services.length) {
			this.pushToDataLayer(DataLayerEvent.RemoveFromCart, services);
		}
	}
}

export default OrderDataLayer;
